import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventService } from '../../services/event.service';
import { Event as CustomEvent, Organisation } from '../../../models/types'; // Alias your custom Event type
import { HttpResponse } from '@angular/common/http';
import Swal from 'sweetalert2';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { faIdBadge, faImage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ProfilePageService } from '../../services/profile-page.service';
import { LoginService } from '../../services/login.service';
import { QuillModule } from 'ngx-quill'
import { OrganisationServiceService } from '../../services/organisation-service.service';
import { co } from '@fullcalendar/core/internal-common';

@Component({
  selector: 'app-add-event',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule, QuillModule],
  templateUrl: './add-event.component.html',
  styleUrls: ['./add-event.component.scss'],
})
export class AddEventComponent {
  newEvent: CustomEvent = {
    eventID: '',
    title: '',
    description: '',
    start: '',
    end: '',
    allDay: true,
    imageRef: '',
    farmID: 0,
    organisationID: -1,
    color: '#000000',
    notify: false,
  };

  faImage = faImage;
  previewImageSrc: string | undefined | ArrayBuffer | null = null;
  imageFile: File | undefined;
  @Input() isOrgAdmin: boolean = false;
  @Input() organisationID: string = "-1";
  @Input() isEditMode: boolean = false;
  @Input() existingEvent: CustomEvent | null = null;

  @Output() eventAdded = new EventEmitter<void>();

  organisations: Organisation[] = []; // Store organisations

  constructor(
    public activeModal: NgbActiveModal, 
    private eventService: EventService, 
    private loginService: LoginService,
    private organisationService: OrganisationServiceService) {}

  ngOnInit() {
    this.loadOrganisations();
    if (this.isEditMode && this.existingEvent) {
      this.newEvent = { ...this.existingEvent };
      this.newEvent.start = this.formatDateForInput(this.newEvent.start);
      this.newEvent.end = this.formatDateForInput(this.newEvent.end);
      this.previewImageSrc = this.newEvent.imageRef; 
    }
  }

  formatDateForInput(dateString: string): string {
    const date = new Date(dateString);
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
    return localISOTime;
  }

  onOrganisationChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedOrganisationID = parseInt(selectElement.value, 10);

    this.newEvent.organisationID = selectedOrganisationID;
    // Set farmID to -1 if the selected organisation is not 'Farm'
    if (selectedOrganisationID !== -1) {
      this.newEvent.farmID = -1;
    }
  }

  isFarmOrganisation(id: any): boolean {
    return Number(id) === -1;
  }

  loadOrganisations() {
    this.loginService.getAllOrganisations().subscribe({
      next: (response) => {
        // Check the response structure
        const orgId = Number(this.organisationID);
        // Extract the organisations array from the response body
        if (response.body && Array.isArray(response.body.organisations)) {
          this.organisations = response.body.organisations;
          if (orgId !== -1) {
            this.organisations = this.organisations.filter(org => org.organisationID === orgId);
          }
        } else {
          console.error('Unexpected response structure:', response.body);
        }
        console.log('Organisations fetched:', this.organisations);
      },
      error: (err) => {
        console.error('Error fetching organisations:', err);
      }
    });
  }
  
  

  formatDate(event: string, field: 'start' | 'end') {
    const formattedDate = new Date(event).toISOString();
    this.newEvent[field] = formattedDate;
  }

  verifyData(newEvent: CustomEvent): boolean {
    if (!newEvent.title || !newEvent.description || !newEvent.start || !newEvent.end ) {
      Swal.fire('Napaka', 'Vsa polja so obvezna', 'error');
      return false;
    }

    if (newEvent.start > newEvent.end) {
      Swal.fire('Napaka', 'Datum začetka ne more biti kasnejši od datuma konca', 'error');
      return false;
    }

    if (this.isFarmOrganisation(newEvent.organisationID)) {
      Swal.fire('Napaka', 'Izberite organizacijo', 'error');
      return false;
    }

    if (!this.imageFile && !this.isEditMode) {
      Swal.fire('Napaka', 'Dodaj sliko dogodka', 'error');
      return false;
    }

    return true;
  }

  addOrUpdateEvent() {
    if (!this.verifyData(this.newEvent)) return;

    if (this.isEditMode) {
      this.updateEvent();
    } else {
      this.createEvent();
    }
  }

  updateEvent() {
    if (!this.verifyData(this.newEvent)) {
      return;
    }

    this.eventService.updateEvent(this.newEvent).subscribe((response) => {
      if (response) {
        Swal.fire('Uspeh', 'Dogodek je bil posodobljen', 'success');

        if (this.imageFile) {
          this.eventService.updateEventImage(Number(this.newEvent.eventID), this.imageFile, this.isOrgAdmin)
            .subscribe((res) => {
              if (res) {
                this.updateImageFile(Number(this.newEvent.eventID)); 
              }
            });
        } else {
          this.eventAdded.emit();
          this.activeModal.close(); 
        }

      } else {
        Swal.fire('Napaka', 'Pri posodabljanju dogodka je prišlo do napake', 'error');
      }
    });
  }


  createEvent() {
    if (!this.verifyData(this.newEvent)) {
      return;
    }
    this.eventService.addEvent(this.newEvent).subscribe((response) => {
      if (response) {
        Swal.fire('Uspeh', 'Dogodek je bil dodan', 'success');
        const event_id = response['event_id'];
        this.uploadImageFile(event_id);
      } else {
        Swal.fire('Napaka', 'Pri dodajanju dogodka je prišlo do napake', 'error');
      }
    });
  }

  updateImageFile(eventID: number) {
    if (this.imageFile) {
      this.eventService.updateEventImage(eventID, this.imageFile, this.isOrgAdmin).subscribe({
        next: () => {
          this.eventAdded.emit();
          this.activeModal.close();
        },
        error: () => Swal.fire('Napaka', 'Napaka pri posodobitvi slike', 'error')
      });
    }
  }

  uploadImageFile(eventID: number) {
    if (this.imageFile) {
        this.eventService.uploadEventImage(eventID, this.imageFile, this.isOrgAdmin).subscribe((response) => {
            if (response) {
              this.eventAdded.emit();
                this.activeModal.close();
            } else {
                Swal.fire('Napaka', 'Napaka pri dodajanju slike', 'error');
            }
        });
    } else {
        Swal.fire('Napaka', 'Dodajanje slike je obvezno', 'error');
    }
  }

  onImageUploadChanged(event: any) {
    const file = event.target.files[0];

    if (file) {
      this.organisationService.compressImage(file, 60, 40).then((compressedFile) => {
        this.imageFile = compressedFile;
        this.previewImageSrc = URL.createObjectURL(compressedFile);
      }).catch(error => console.error('Error compressing image:', error));
    }
  }
}
